// The talent tree: which nodes can be taken or given back, what the taken ones add up to, and how each stat applies.
// The module applies them; the client predicts the ones it must (attack speed and cost) and shows the rest.
import { AttackKind, TalentStat, TalentTier, WeaponClass } from '../constants';
import type { GameContent, ItemDefinition, TalentEffect } from '../content';
import { Defense } from './items';
import { Leveling } from './progress';

const f = Math.fround;
const statCount = Object.values(TalentStat).filter(v => typeof v === 'number').length;
/** Percent scaling, never below nothing. */
const percent = (value: number, pct: number) => value * Math.max(0, 100 + pct) / 100;

/** Each TalentStat's total. */
export type TalentBonuses = readonly number[];

export const TalentRules = {
  /** One point each level after the first. */
  pointsForLevel: (level: number) => Leveling.clampLevel(level) - 1,
  unspent: (level: number, allocated: readonly number[]) => TalentRules.pointsForLevel(level) - allocated.length,
  /** Copper to give back one talent. */
  refundCost: (level: number) => 10 * Leveling.clampLevel(level),
  /** How far auras reach before AuraRange. */
  auraRangeTiles: 10,
  baseHealthRegen: 0.5,
  baseManaRegen: 2,
  baseCritMultiplier: 1.5,
  /** The stats an aura may carry: ones the server alone applies (never predicted attack speed or cost). */
  auraStats: [TalentStat.HealthRegen, TalentStat.ManaRegen, TalentStat.Armor, TalentStat.ArmorPercent, TalentStat.Damage,
    TalentStat.DamageTakenPercent, TalentStat.HealingDone] as readonly TalentStat[],
};

export const noBonuses: TalentBonuses = Object.freeze(new Array(statCount).fill(0));

/** What the allocated talents (and auras from others nearby, which never stack with the same node) add up to. */
export function talentBonuses(content: GameContent, allocated: readonly number[], auras: readonly number[] = []): TalentBonuses {
  const sum = new Array(statCount).fill(0), seen = new Set<number>();
  const add = (id: number, auraOnly: boolean) => {
    const node = content.talent(id);
    if (!node || seen.has(id) || (auraOnly && !node.aura)) return;
    seen.add(id);
    for (const e of node.effects) if (e.stat >= 0 && e.stat < statCount) sum[e.stat] += e.value;
  };
  for (const id of allocated) add(id, false);
  for (const id of auras) add(id, true);
  return sum;
}

// ---- the tree ----------------------------------------------------------------------------------------------------

/** Why a node can't be taken now, or null. */
export function allocateProblem(content: GameContent, level: number, allocated: readonly number[], id: number): string | null {
  const node = content.talent(id);
  if (!node || node.tier === TalentTier.Start) return 'There is no such talent.';
  if (allocated.includes(id)) return 'You already have that talent.';
  if (TalentRules.unspent(level, allocated) <= 0) return 'No talent points to spend. You get one each level.';
  if (!node.links.some(l => l === content.talentStart || allocated.includes(l))) return "That talent isn't connected to your tree yet.";
  return null;
}

/** Why a node can't be given back, or null: the rest must still reach the start. */
export function refundProblem(content: GameContent, allocated: readonly number[], id: number): string | null {
  if (!allocated.includes(id)) return "You don't have that talent.";
  const rest = allocated.filter(a => a !== id);
  return connected(content, rest).length === rest.length ? null : 'Other talents depend on that one: give those back first.';
}

/** The allocated nodes the start reaches through allocated nodes. */
function connected(content: GameContent, allocated: readonly number[]): number[] {
  const have = new Set(allocated), reached = new Set<number>(), queue = [content.talentStart];
  while (queue.length) {
    const node = content.talent(queue.pop()!);
    for (const l of node?.links ?? []) if (have.has(l) && !reached.has(l)) { reached.add(l); queue.push(l); }
  }
  return allocated.filter(a => reached.has(a));
}

/**
 * A saved tree made valid for the content in effect: unknown, repeated and cut-off nodes go; more than the level
 * allows gives every point back.
 */
export function validTree(content: GameContent, level: number, allocated: readonly number[]): number[] {
  const known = [...new Set(allocated)].filter(id => { const n = content.talent(id); return n && n.tier !== TalentTier.Start; });
  const kept = connected(content, known);
  return kept.length > TalentRules.pointsForLevel(level) ? [] : kept;
}

// ---- what the stats do -------------------------------------------------------------------------------------------

export const TalentStats = {
  maxHealth: (base: number, level: number, b: TalentBonuses) =>
    Math.max(1, Math.floor(percent(Leveling.playerMaxHealth(base, level) + b[TalentStat.MaxHealth], b[TalentStat.MaxHealthPercent]))),
  maxMana: (base: number, level: number, b: TalentBonuses) =>
    Math.max(0, Math.floor(percent(Leveling.playerMaxMana(base, level) + b[TalentStat.MaxMana], b[TalentStat.MaxManaPercent]))),
  /** Per second. */
  healthRegen: (b: TalentBonuses) => Math.max(0, TalentRules.baseHealthRegen + b[TalentStat.HealthRegen]),
  manaRegen: (b: TalentBonuses) => Math.max(0, TalentRules.baseManaRegen + b[TalentStat.ManaRegen]),
  armor: (gear: number, b: TalentBonuses) => Math.max(0, percent(gear + b[TalentStat.Armor], b[TalentStat.ArmorPercent])),
  blockArmor: (shield: number, b: TalentBonuses) => percent(shield, b[TalentStat.BlockArmorPercent]),
  /** Motor stamina units a blocked hit costs. */
  blockStaminaUnits: (b: TalentBonuses) => Math.round(percent(Defense.blockStaminaUnits, b[TalentStat.BlockCostPercent])),
  /** Damage taken after armour (float32, like health); never below a quarter. */
  damageTaken: (damage: number, b: TalentBonuses) => f(percent(damage, Math.max(-75, b[TalentStat.DamageTakenPercent]))),
  potionHealing: (heals: number, b: TalentBonuses) => percent(heals, b[TalentStat.PotionHealing]),
  healingDone: (amount: number, b: TalentBonuses) => f(percent(amount, b[TalentStat.HealingDone])),
  auraRangePixels: (b: TalentBonuses) => percent(TalentRules.auraRangeTiles * 16, b[TalentStat.AuraRange]),
  /** 0..1. */
  critChance: (b: TalentBonuses) => Math.max(0, Math.min(100, b[TalentStat.CritChance])) / 100,
  critMultiplier: (b: TalentBonuses) => Math.max(1, TalentRules.baseCritMultiplier + b[TalentStat.CritDamage] / 100),
  healthOnHit: (b: TalentBonuses) => Math.max(0, b[TalentStat.HealthOnHit]),
  thorns: (b: TalentBonuses) => Math.max(0, b[TalentStat.Thorns]),
};

/** What kind of attack an item makes: its spell, its projectile, or its swing. */
export function attackKindOf(def: ItemDefinition | undefined): AttackKind {
  if (def?.spell) return AttackKind.Spell;
  if (def?.weapon?.projectile || def?.weapon?.weaponClass === WeaponClass.Bow) return AttackKind.Ranged;
  return AttackKind.Melee;
}
const damageStat = [TalentStat.MeleeDamage, TalentStat.RangedDamage, TalentStat.SpellDamage];
const speedStat = [TalentStat.MeleeSpeed, TalentStat.RangedSpeed, TalentStat.CastSpeed];

export const TalentAttacks = {
  damage: (base: number, kind: AttackKind, b: TalentBonuses) => f(percent(base, b[TalentStat.Damage] + b[damageStat[kind]])),
  knockback: (base: number, b: TalentBonuses) => f(percent(base, b[TalentStat.Knockback])),
  /** Ticks between attacks: faster by the kind's speed (never more than ten times). */
  cooldownTicks: (cooldownSeconds: number, kind: AttackKind, b: TalentBonuses) =>
    Math.max(1, Math.ceil(f(f(cooldownSeconds) * 30) * 100 / (100 + Math.max(-50, Math.min(900, b[speedStat[kind]]))))),
  /** What an attack costs: mana, or health for spells under SpellsCostHealth. */
  cost(manaCost: number, kind: AttackKind, b: TalentBonuses): { mana: number; health: number } {
    const cost = f(percent(manaCost, b[TalentStat.ManaCostPercent]));
    return kind === AttackKind.Spell && b[TalentStat.SpellsCostHealth] > 0 ? { mana: 0, health: cost } : { mana: cost, health: 0 };
  },
};

// ---- words -------------------------------------------------------------------------------------------------------

const statText: Record<TalentStat, [string, boolean]> = {
  [TalentStat.MaxHealth]: ['maximum health', false], [TalentStat.MaxHealthPercent]: ['maximum health', true],
  [TalentStat.MaxMana]: ['maximum mana', false], [TalentStat.MaxManaPercent]: ['maximum mana', true],
  [TalentStat.HealthRegen]: ['health regenerated per second', false], [TalentStat.ManaRegen]: ['mana regenerated per second', false],
  [TalentStat.Armor]: ['armour', false], [TalentStat.ArmorPercent]: ['armour', true],
  [TalentStat.BlockArmorPercent]: ['shield block armour', true], [TalentStat.BlockCostPercent]: ['stamina cost of blocking', true],
  [TalentStat.Damage]: ['damage', true], [TalentStat.MeleeDamage]: ['melee damage', true],
  [TalentStat.RangedDamage]: ['ranged damage', true], [TalentStat.SpellDamage]: ['spell damage', true],
  [TalentStat.MeleeSpeed]: ['melee attack speed', true], [TalentStat.RangedSpeed]: ['ranged attack speed', true],
  [TalentStat.CastSpeed]: ['cast speed', true], [TalentStat.CritChance]: ['critical hit chance', true],
  [TalentStat.CritDamage]: ['critical hit damage', true], [TalentStat.ManaCostPercent]: ['mana cost', true],
  [TalentStat.HealthOnHit]: ['health gained per hit', false], [TalentStat.Thorns]: ['damage dealt back to attackers', false],
  [TalentStat.PotionHealing]: ['potion healing', true], [TalentStat.HealingDone]: ['healing done', true],
  [TalentStat.AuraRange]: ['aura range', true], [TalentStat.Knockback]: ['knockback', true],
  [TalentStat.SpellsCostHealth]: ['', false], [TalentStat.DamageTakenPercent]: ['damage taken', true],
};
const number = (v: number) => (Number.isInteger(v) ? String(v) : v.toFixed(1).replace(/\.0$/, ''));

/** "+8 maximum health", "-10% mana cost". */
export function describeEffect(e: TalentEffect): string {
  if (e.stat === TalentStat.SpellsCostHealth) return 'Spells cost health instead of mana';
  const [what, pct] = statText[e.stat] ?? [`stat ${e.stat}`, false];
  return `${e.value < 0 ? '-' : '+'}${number(Math.abs(e.value))}${pct ? '%' : ''} ${what}`;
}
/** Whether an effect helps (a lower cost or less damage taken does). */
export function effectGood(e: TalentEffect): boolean {
  const lowerIsBetter = e.stat === TalentStat.BlockCostPercent || e.stat === TalentStat.ManaCostPercent || e.stat === TalentStat.DamageTakenPercent;
  return e.stat === TalentStat.SpellsCostHealth || (e.value < 0) === lowerIsBetter;
}
